/**
 * 「提交时会原样附加这些」—— 最终指令对用户可见(2026-07-29 T2)。
 *
 * ═══ 改之前的事实 ═══════════════════════════════════════════════════════════
 * 三段东西是**提交那一刻**才拼进 `rawPrompt` 的,界面上一个字都不显示:
 *   【产品】 `productAdapter.productPromptSection()`  ← 产品库原文逐字
 *   【风格】 L1 画风 rich tone(200-400 字)+ L2 配色 hex
 *   【字体风格】 `fontPhraseForKey()`
 * AI 优化那条路**故意**绕过 Qwen(产品参数一个字都不许被 LLM 改写),于是
 * `OptimizedContentSection` 里显示的两段也不含产品 —— 用户选了产品点了优化,
 * 界面上和「没选产品」长得完全一样。
 *
 * 连带的坑:`rawPrompt` 上限 2000(ai `domain/models.py:63`),超了是 422、
 * 用户只看到「生成失败」。而这三段**一个字都不算在文案框那个 maxLength 计数器里** ——
 * M7 手表那种 12 条卖点的产品,光【产品】段就 800+ 字。
 *
 * ═══ 为什么是「附加段」而不是把它们塞进文案框 ═══════════════════════════════
 * 400 字的画风文本塞进用户的写作区会淹没用户自己写的话。所以做成同一张卡里的第二层:
 * 摘要常驻(收起也能看到「现在会带什么进去」)、全文可展开、**渲染的是
 * `buildRawPrompt` 的真实输出**(同一个纯函数,不另写一份 —— 写第二份必漂)。
 *
 * ★不做双向绑定。选择器是这些段的真相源;想手写的用户点「改成我自己写」,
 *   那一段全文落进主文案框 + 对应选择器复位,此后不再自动附加。
 *   做成双绑必然出现「用户改了那段又被选择器覆盖」——button-as-state 的同型。
 */
import { useState } from "react";
import { ChevronDown, PencilLine } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import {
  absorbedMaterials,
  buildRawPrompt,
  RAW_PROMPT_MAX,
  type AbsorbedMaterials,
} from "../../../model/free-create/buildRawPrompt";
import type { StyleTokens } from "../../../longimage/model/canvas";
import {
  productPromptSection,
  wantsProductSpecsOnImage,
  type FreeCreateProduct,
} from "../../../model/free-create/productAdapter";
import { composeStylePrompt } from "../../../model/style/tokensToPrompt";
import {
  ratioChannelLabel,
  ratioCompositionPhrase,
} from "../../../model/free-create/ratioComposition";
import { fontPhraseForKey } from "./fontStyleOptions";

export interface FinalPromptFoldProps {
  /** 用户文案(RAW 路径正文)。 */
  prompt: string;
  isAiOptimized: boolean;
  contentDescription: string;
  systemConstraints: string;
  product: FreeCreateProduct | null;
  /** 「在图上显示产品参数」勾选 —— 与提交侧同一个判据(见 buildRawPrompt)。 */
  showProductSpecs?: boolean;
  selectedStyle: string | null;
  styleTokens: StyleTokens | null;
  fontStyle: string;
  /** 归整层已吸收的素材快照(prompt-assembly W1);吸收过的段不再作为「追加段」展示。 */
  absorbed: AbsorbedMaterials | null;
  /**
   * 本次生效的画幅 —— 渲染【画幅构图】段。与 `buildRawPrompt` 同一个入参,
   * 这一列显示的就是提交时真会发出去的那串(口径漂了就是在骗用户)。
   */
  ratio?: string | null;
  /** 「改成我自己写」:把该段全文并进主文案框 + 复位对应选择器。 */
  onInline: (segment: "product" | "style" | "font", text: string) => void;
  /** 右回显列里默认展开(那列有纵向空间,而它是「左边改什么、右边变什么」的主体)。 */
  defaultOpen?: boolean;
}

interface Segment {
  key: "product" | "ratio" | "style" | "font";
  label: string;
  summary: string;
  body: string;
  /** 这一段是不是「不参与 AI 改写」—— 产品段在 AI 优化路径上也逐字发,要说清楚。 */
  verbatim?: boolean;
  /**
   * 不给「改成我自己写」。
   * ★画幅构图段是从画幅**派生**的,不是一份可以接管的文本:把它并进文案框之后,
   *   用户再改画幅,文案框里那句旧法则不会跟着变,于是指令里出现两条互相打架的版面
   *   规矩(线上那两段【风格】对撞就是同型)。要改版面就去改画幅。
   */
  noEject?: boolean;
}

export function FinalPromptFold({
  prompt,
  isAiOptimized,
  contentDescription,
  systemConstraints,
  product,
  showProductSpecs = false,
  selectedStyle,
  styleTokens,
  fontStyle,
  absorbed,
  ratio,
  onInline,
  defaultOpen = false,
}: FinalPromptFoldProps) {
  const [open, setOpen] = useState(defaultOpen);

  // ★总长度必须走 buildRawPrompt 本体 —— 它才是提交时真正发出去的那串。
  //   在这里重拼一遍「大概是这些段」只会和真相漂移(而漂移的后果是 422)。
  const rawPrompt = buildRawPrompt({
    isAiOptimized,
    prompt,
    contentDescription,
    systemConstraints,
    selectedStyle,
    styleTokens,
    fontStyle,
    product,
    showProductSpecs,
    absorbed,
    ratio,
  });
  const total = rawPrompt.length;
  const over = total > RAW_PROMPT_MAX;

  // ★「这一段还会不会被追加」必须与 buildRawPrompt 用**同一个判据**:归整层吸收过的
  //   素材已经在 AI 优化产物内部,不再作为追加段存在。这里多列一段,就是在告诉用户
  //   「产品会被附加在末尾」而实际不会 —— 显示口径与提交口径不一致就是在骗用户。
  const current = absorbedMaterials({ product, selectedStyle, styleTokens, fontStyle });
  const inForce = isAiOptimized ? absorbed : null;
  const productAbsorbed = inForce !== null && inForce.productCode === current.productCode;
  const styleAbsorbed = inForce !== null && inForce.styleSignature === current.styleSignature;

  const segments: Segment[] = [];
  if (product && !productAbsorbed) {
    const body = productPromptSection(product, {
      showSpecs: showProductSpecs || wantsProductSpecsOnImage(prompt),
    });
    segments.push({
      key: "product",
      label: "产品",
      summary: [
        product.name,
        product.sellingPoints.length ? `${product.sellingPoints.length} 条卖点` : "",
        product.specs.length ? `${product.specs.length} 项参数` : "",
      ]
        .filter(Boolean)
        .join(" · "),
      body,
      verbatim: true,
    });
  }
  // 【画幅构图】—— 段序与 buildRawPrompt 一致:产品之后、风格之前。
  // ★2026-09-09 拍板留白句全拆后构图短语只看画幅(0907 的条件性 prompt 参已作废);
  //   预览与真实提交仍同源同判(所见即所发)。
  const compositionBody = ratioCompositionPhrase(ratio);
  if (compositionBody) {
    segments.push({
      key: "ratio",
      label: "画幅构图",
      summary: [ratio, ratioChannelLabel(ratio)].filter(Boolean).join(" · "),
      body: compositionBody,
      verbatim: true,
      noEject: true,
    });
  }

  // ★AI 优化路径 L1 已经过了归整层,不再注入(再拼一遍是 double)。
  const styleBody = styleAbsorbed
    ? null
    : composeStylePrompt(isAiOptimized ? null : selectedStyle, styleTokens);
  if (styleBody) {
    segments.push({
      key: "style",
      label: "风格",
      summary: [
        isAiOptimized ? "已并入 AI 优化文案" : selectedStyle?.trim() ? "已选画风" : "",
        styleTokens ? "已定配色" : "",
      ]
        .filter(Boolean)
        .join(" · "),
      body: styleBody,
    });
  }
  const fontBody = styleAbsorbed ? "" : fontPhraseForKey(fontStyle);
  if (fontBody) {
    segments.push({ key: "font", label: "字体风格", summary: "已选字体档", body: fontBody });
  }

  const appended = segments.reduce((n, s) => n + s.body.length, 0);

  if (segments.length === 0) {
    // 一段都没有 = 文案框里就是全部 —— 只给字数,不摆一个空壳折叠区
    return (
      <div className="flex justify-end px-1 pt-1">
        <CharCount total={total} over={over} />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-dashed border-border/70 bg-muted/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
        <span className="text-[12.5px] font-medium text-foreground">
          提交时会原样附加这些
          <span className="ml-1 font-normal text-muted-foreground">(+{appended} 字)</span>
        </span>
        <span className="min-w-0 flex-1 truncate text-right text-[12.5px] text-muted-foreground">
          {segments.map((s) => `【${s.label}】${s.summary}`).join("　")}
        </span>
      </button>

      {open && (
        <div className="space-y-2 px-3 pb-3">
          {segments.map((s) => {
            // 可接管的段 key(收窄给 onInline);noEject 段与画幅构图段拿不到按钮 ——
            // 它是从画幅派生的,并进文案框之后改画幅不会跟着变,等于埋一条打架的版面规矩。
            const ejectKey = s.noEject || s.key === "ratio" ? null : s.key;
            return (
              <div key={s.key} className="rounded-md border border-border/50 bg-background/50 p-2">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-[12.5px] font-medium text-foreground">【{s.label}】</span>
                  <div className="flex items-center gap-2">
                    {s.verbatim && (
                      <span className="text-[12px] text-muted-foreground">
                        不参与 AI 改写,逐字发给引擎
                      </span>
                    )}
                    {ejectKey && (
                      <button
                        type="button"
                        onClick={() => onInline(ejectKey, s.body)}
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        title="把这段并进上面的文案框,之后由你自己改"
                      >
                        <PencilLine className="h-3.5 w-3.5" />
                        改成我自己写
                      </button>
                    )}
                  </div>
                </div>
                <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words font-sans text-[12.5px] leading-relaxed text-muted-foreground">
                  {s.body}
                </pre>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end border-t border-border/40 px-3 py-1.5">
        <CharCount total={total} over={over} longest={segments} />
      </div>
    </div>
  );
}

function CharCount({
  total,
  over,
  longest,
}: {
  total: number;
  over: boolean;
  longest?: Segment[];
}) {
  // 超限时指出**是哪一段撑爆的** —— 只报「超了 300 字」用户不知道该删哪里
  const biggest =
    over && longest?.length ? [...longest].sort((a, b) => b.body.length - a.body.length)[0] : null;
  return (
    <span
      className={cn(
        "text-[12.5px] tabular-nums",
        over ? "font-medium text-destructive" : "text-muted-foreground"
      )}
    >
      最终指令 {total} / {RAW_PROMPT_MAX} 字
      {over && (
        <>
          {" "}
          · 超了 {total - RAW_PROMPT_MAX} 字,提交会失败
          {biggest && `(最长的是【${biggest.label}】${biggest.body.length} 字)`}
        </>
      )}
    </span>
  );
}
